import React from "react";
import { FaEye, FaHistory } from "react-icons/fa";
import { GiBlackBelt } from "react-icons/gi";

import {
  DEFAULT_AVATAR,
  formatGradeDate,
  getAdherentName,
  getAdherentPhoto,
  getBeltImage,
  getBeltName,
  getGroupNames,
} from "../utils/passageGradeCoachUtils";

function CoachGradeTable({
  rows = [],
  selectedAdherentIds = [],
  selectedFilteredCount = 0,
  onToggleSelect,
  onSelectAllFiltered,
  onUnselectAllFiltered,
  onOpenHistory,
}) {
  const selectedSet = new Set(selectedAdherentIds.map(String));

  const allFilteredSelected =
    rows.length > 0 &&
    rows.every((row) => selectedSet.has(String(row.adherent.id)));

  return (
    <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/20">
      <div className="flex flex-col gap-3 border-b border-slate-200 p-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-200">
            Tableau coach
          </p>

          <h2 className="mt-1 font-titre text-xl font-bold text-slate-950 dark:text-white">
            {rows.length} judoka{rows.length > 1 ? "s" : ""}
          </h2>

          <p className="mt-1 text-xs font-bold text-slate-400">
            Clique sur Historique pour gérer les ceintures d’un judoka.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={
              allFilteredSelected ? onUnselectAllFiltered : onSelectAllFiltered
            }
            disabled={!rows.length}
            className="inline-flex min-h-10 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:border-sky-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            {allFilteredSelected
              ? "Désélectionner filtrés"
              : "Sélectionner filtrés"}
          </button>

          <span className="inline-flex min-h-10 items-center justify-center rounded-2xl bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 dark:bg-sky-500/10 dark:text-sky-200">
            {selectedFilteredCount} sélectionné
            {selectedFilteredCount > 1 ? "s" : ""}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:border-white/10 dark:bg-white/[0.03]">
              <th className="sticky left-0 z-20 bg-slate-50 px-4 py-3 dark:bg-slate-900">
                Sélection
              </th>
              <th className="px-4 py-3">Judoka</th>
              <th className="px-4 py-3">Groupe</th>
              <th className="px-4 py-3">Ceinture actuelle</th>
              <th className="px-4 py-3">Dernier passage</th>
              <th className="px-4 py-3">Historique</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-white/10">
            {rows.map((row) => {
              const lastGrade = row.lastGrade;
              const beltImage = lastGrade ? getBeltImage(lastGrade) : null;
              const isSelected = selectedSet.has(String(row.adherent.id));

              return (
                <tr
                  key={row.adherent.id}
                  className={`text-sm transition ${
                    isSelected
                      ? "bg-blue-50/70 dark:bg-sky-500/10"
                      : "hover:bg-sky-50/60 dark:hover:bg-white/[0.03]"
                  }`}
                >
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 dark:bg-slate-900">
                    <button
                      type="button"
                      onClick={() => onToggleSelect(row.adherent.id)}
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl border text-lg font-bold transition ${
                        isSelected
                          ? "border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                          : "border-slate-300 bg-white text-transparent hover:border-blue-300 dark:border-white/10 dark:bg-slate-950"
                      }`}
                      aria-label={`Sélectionner ${getAdherentName(
                        row.adherent,
                      )}`}
                    >
                      ✓
                    </button>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex min-w-[220px] items-center gap-3">
                      <img
                        src={getAdherentPhoto(row.adherent)}
                        alt={getAdherentName(row.adherent)}
                        className="h-11 w-11 rounded-2xl object-cover"
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_AVATAR;
                        }}
                      />

                      <div className="min-w-0">
                        <p className="truncate font-bold text-slate-950 dark:text-white">
                          {getAdherentName(row.adherent)}
                        </p>

                        <p className="mt-0.5 truncate text-xs font-medium text-slate-400">
                          {row.adherent.club || "Club non renseigné"}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="max-w-[240px] px-4 py-3">
                    <p className="line-clamp-2 text-xs font-bold text-slate-600 dark:text-white/60">
                      {getGroupNames(row)}
                    </p>
                  </td>

                  <td className="px-4 py-3">
                    {lastGrade ? (
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-24 items-center justify-center rounded-2xl bg-slate-50 dark:bg-white/[0.04]">
                          <img
                            src={beltImage}
                            alt={getBeltName(lastGrade)}
                            className="h-9 w-full object-contain"
                          />
                        </div>

                        <span className="font-bold text-slate-800 dark:text-white/80">
                          {getBeltName(lastGrade)}
                        </span>
                      </div>
                    ) : (
                      <span className="inline-flex min-h-9 items-center gap-2 rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-400 dark:bg-white/10 dark:text-white/35">
                        <GiBlackBelt />
                        Non renseignée
                      </span>
                    )}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-600 dark:text-white/60">
                    {lastGrade ? formatGradeDate(lastGrade.date_passage) : "—"}
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex min-h-9 items-center gap-2 rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60">
                      <FaHistory />
                      {row.totalGrades} passage{row.totalGrades > 1 ? "s" : ""}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onOpenHistory(row)}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-blue-800"
                    >
                      <FaEye />
                      Historique
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default CoachGradeTable;